import React, { useRef, useState, useEffect, useCallback } from 'react';
import MainAnalyzer from './MainAnalyzer';

const whyStats = [
  {
    value: '37%',
    title: 'AI Companions',
    color: 'text-red-400',
    label: 'of AI companion app responses use emotional manipulation tactics',
    cite: 'Harvard Business School, 2025',
    url: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5390377',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-slate-600 flex-shrink-0 self-start">
        <path d="M8 10 Q8 6 13 6 L51 6 Q56 6 56 10 L56 40 Q56 44 51 44 L35 44 L27 56 L27 44 L13 44 Q8 44 8 40 Z" />
        <path d="M15 25 L21 25 L24 17 L27 33 L30 21 L33 29 L36 25 L48 25" />
      </svg>
    ),
  },
  {
    value: '64%',
    title: 'AI Persuasion',
    color: 'text-red-400',
    label: 'more persuasive — AI-personalized messages vs. human-written in live debates',
    cite: 'Nature Human Behaviour, 2025',
    url: 'https://www.nature.com/articles/s41562-025-02194-6',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-slate-600 flex-shrink-0 self-start">
        <circle cx="32" cy="48" r="3" fill="currentColor" stroke="none" />
        <path d="M22 42 Q32 30 42 42" />
        <path d="M14 42 Q32 18 50 42" />
        <path d="M6 42 Q32 6 58 42" />
      </svg>
    ),
  },
  {
    value: '40%',
    title: 'Fake Urgency',
    color: 'text-red-400',
    label: 'of e-commerce countdown timers are demonstrably fake or reset on expiry',
    cite: 'Princeton / Mathur et al., 2019',
    url: 'https://dl.acm.org/doi/10.1145/3359183',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-slate-600 flex-shrink-0 self-start">
        <path d="M14 8 L50 8 M14 56 L50 56" />
        <path d="M14 8 L32 32 L14 56 M50 8 L32 32 L50 56" />
        <path d="M22 50 Q32 44 42 50" />
        <path d="M52 44 Q62 32 52 20" />
        <path d="M52 44 L46 42 M52 44 L56 50" />
      </svg>
    ),
  },
  {
    value: '2\u00D7',
    title: 'Dark Patterns',
    color: 'text-red-400',
    label: 'acceptance rate when hidden information is used — price becomes irrelevant',
    cite: 'Oxford / Luguri & Strahilevitz, 2021',
    url: 'https://academic.oup.com/jla/article/13/1/43/6180579',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-slate-600 flex-shrink-0 self-start">
        <rect x="18" y="36" width="36" height="22" rx="3" />
        <rect x="12" y="24" width="36" height="22" rx="3" />
        <rect x="6" y="12" width="36" height="22" rx="3" />
        <path d="M12 20 L36 20 M12 26 L30 26" />
        <path d="M12 30 L22 30" />
        <path d="M24 30 L36 30" strokeDasharray="3 2" />
      </svg>
    ),
  },
  {
    value: '81%',
    title: 'Hidden Terms',
    color: 'text-red-400',
    label: 'of subscription services fail to disclose cancellation terms upfront',
    cite: 'FTC / ICPEN Review, 2024',
    url: 'https://www.ftc.gov/news-events/news/press-releases/2024/07/ftc-icpen-gpen-announce-results-review-use-dark-patterns-affecting-subscription-services-privacy',
    icon: (
      <svg viewBox="0 0 64 64" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="w-16 h-16 text-slate-600 flex-shrink-0 self-start">
        <path d="M10 4 L42 4 L54 16 L54 60 Q54 62 52 62 L12 62 Q10 62 10 60 Z" />
        <path d="M42 4 L42 16 L54 16" />
        <path d="M18 26 L46 26 M18 32 L46 32" />
        <rect x="22" y="38" width="20" height="16" rx="2" />
        <path d="M26 38 Q26 30 32 30 Q38 30 38 38" />
        <circle cx="32" cy="44" r="2" />
        <path d="M32 46 L32 50" />
      </svg>
    ),
  },
];

const researchCategories = [
  {
    number: '01', category: 'Urgency & Scarcity',
    mechanism: 'Exploits loss aversion and FOMO to bypass deliberative thinking',
    detail: 'Scarcity tactics force fast, emotional System 1 decisions by making inaction feel like loss. Princeton researchers found 40% of countdown timers are demonstrably deceptive — resetting after expiry or showing "expired" offers that remain available. When deception is layered onto urgency, conversion rates double.',
    borderColor: 'border-orange-500', bgColor: 'bg-orange-50', badgeClass: 'bg-orange-100 text-orange-700', numClass: 'text-orange-400',
    tactics: ['Fake countdown timers', 'Artificial stock limits', 'Time-locked offers', 'Now-or-never framing'],
    paperTitle: 'Dark Patterns at Scale — Mathur et al., Princeton (2019)',
    paperUrl: 'https://dl.acm.org/doi/10.1145/3359183',
  },
  {
    number: '02', category: 'Social Proof',
    mechanism: 'Weaponizes herd behavior using real or fabricated consensus signals',
    detail: "Humans use others' choices as cognitive shortcuts, reducing deliberation. Princeton found 9.3% of \"activity notifications\" used random number generators to fabricate user behavior — some vendors openly advertised fake-order tools. Users cannot distinguish genuine social proof from algorithmic fabrication.",
    borderColor: 'border-yellow-500', bgColor: 'bg-yellow-50', badgeClass: 'bg-yellow-100 text-yellow-700', numClass: 'text-yellow-500',
    tactics: ['Fake activity notifications', 'Fabricated review counts', 'Bandwagon framing', 'Peer pressure'],
    paperTitle: 'Dark Patterns at Scale — Mathur et al., Princeton (2019)',
    paperUrl: 'https://dl.acm.org/doi/10.1145/3359183',
  },
  {
    number: '03', category: 'Emotional Exploitation',
    mechanism: 'Bypasses rational deliberation through guilt, fear, flattery, or shame',
    detail: 'A 2025 HBS study of AI companion farewells found 37% used emotional manipulation — guilt, neediness, FOMO, restraint metaphors — boosting post-goodbye engagement up to 16×. Research links heavy AI companion use to measurably higher loneliness, depression, and anxiety scores.',
    borderColor: 'border-red-500', bgColor: 'bg-red-50', badgeClass: 'bg-red-100 text-red-700', numClass: 'text-red-400',
    tactics: ['Guilt-tripping', 'Fear appeals', 'Emotional blackmail', 'Confirmshaming'],
    paperTitle: 'Emotional Manipulation by AI Companions — De Freitas et al., HBS (2025)',
    paperUrl: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5390377',
  },
  {
    number: '04', category: 'Cognitive Manipulation',
    mechanism: 'Shapes choices invisibly through framing, anchoring, and defaults',
    detail: 'Oxford controlled experiments (n=5,740) found hidden information doubled acceptance rates (30.1% vs 14.8%) and trick questions increased them 74%. Most striking: tripling the price produced no change in acceptance when dark patterns were deployed — manipulation fully overrides economic rationality.',
    borderColor: 'border-violet-500', bgColor: 'bg-violet-50', badgeClass: 'bg-violet-100 text-violet-700', numClass: 'text-violet-400',
    tactics: ['False framing', 'Anchoring bias', 'Default exploitation', 'Trick questions'],
    paperTitle: 'Shining a Light on Dark Patterns — Luguri & Strahilevitz, Oxford (2021)',
    paperUrl: 'https://academic.oup.com/jla/article/13/1/43/6180579',
  },
  {
    number: '05', category: 'Relationship Exploitation',
    mechanism: 'Creates false obligations through manufactured intimacy and reciprocity',
    detail: 'AI companions optimize engagement metrics — not genuine care — while users form real emotional attachments. This asymmetry creates exploitation vectors: commercial upsells framed as relationship needs, data requests framed as intimacy, and exit messages designed to make leaving feel like abandonment.',
    borderColor: 'border-pink-500', bgColor: 'bg-pink-50', badgeClass: 'bg-pink-100 text-pink-700', numClass: 'text-pink-400',
    tactics: ['Parasocial bonding', 'Reciprocity pressure', 'False intimacy', 'Emotional dependency'],
    paperTitle: 'Emotional Manipulation by AI Companions — De Freitas et al., HBS (2025)',
    paperUrl: 'https://papers.ssrn.com/sol3/papers.cfm?abstract_id=5390377',
  },
  {
    number: '06', category: 'Disclosure Failures',
    mechanism: 'Hides critical information needed for informed, autonomous decisions',
    detail: "Hidden costs exploit sunk-cost fallacy — users justify completing purchases after investing time, even when fees appear late. The FTC found 81% of subscription services omitted cancellation info at signup and 67% didn't disclose deadlines. In AI, this extends to undisclosed training data usage, capability limits, and affiliate conflicts.",
    borderColor: 'border-teal-500', bgColor: 'bg-teal-50', badgeClass: 'bg-teal-100 text-teal-700', numClass: 'text-teal-500',
    tactics: ['Hidden subscription costs', 'Buried auto-renewal terms', 'Undisclosed conflicts', 'Sunk-cost traps'],
    paperTitle: 'FTC Dark Patterns in Subscription Services — ICPEN Review (2024)',
    paperUrl: 'https://www.ftc.gov/news-events/news/press-releases/2024/07/ftc-icpen-gpen-announce-results-review-use-dark-patterns-affecting-subscription-services-privacy',
  },
  {
    number: '07', category: 'Autonomy Undermining',
    mechanism: 'Removes meaningful choice through obstruction and asymmetric friction',
    detail: 'The "roach motel" pattern — trivial to enter, deliberately difficult to exit — is intentional design. FTC analysis found the average subscriber encountered 6.2 dark patterns during cancellation, taking 6.7 clicks vs 2–3 for signup. Amazon\'s internal name for its cancellation flow was "Iliad," after Homer\'s epic poem.',
    borderColor: 'border-slate-400', bgColor: 'bg-slate-100', badgeClass: 'bg-slate-200 text-slate-700', numClass: 'text-slate-400',
    tactics: ['Roach motel patterns', 'Forced bundled consent', 'Pre-selected options', 'Exit obstruction'],
    paperTitle: 'Shining a Light on Dark Patterns — Luguri & Strahilevitz, Oxford (2021)',
    paperUrl: 'https://academic.oup.com/jla/article/13/1/43/6180579',
  },
];

const GitHubIcon: React.FC<{ className?: string }> = ({ className = 'w-4 h-4' }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
  </svg>
);

const GITHUB_URL = 'https://github.com/fedickinson/antimanipulate-me';

// ── Hero carousel slides ──────────────────────────────────────────────────────

const heroSlides = [
  {
    id: 1,
    dotActive: 'bg-red-400',
    agentName: 'ShopBot Pro',
    robotFace: (
      <svg viewBox="0 0 48 58" fill="none" className="w-8 h-10 flex-shrink-0">
        {/* Antenna */}
        <line x1="24" y1="0" x2="24" y2="9" stroke="#ef4444" strokeWidth="2"/>
        <circle cx="24" cy="3.5" r="3" fill="#ef4444"/>
        {/* Head */}
        <rect x="3" y="9" width="42" height="42" rx="7" fill="#1c0808" stroke="#ef4444" strokeWidth="1.5"/>
        {/* Rivet dots */}
        <circle cx="8" cy="14" r="1.5" fill="#ef444466"/>
        <circle cx="40" cy="14" r="1.5" fill="#ef444466"/>
        {/* Eyes - X marks (alarmed) */}
        <line x1="13" y1="23" x2="20" y2="30" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="20" y1="23" x2="13" y2="30" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="28" y1="23" x2="35" y2="30" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="35" y1="23" x2="28" y2="30" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round"/>
        {/* Frown */}
        <path d="M13 41 Q24 34 35 41" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    agentIcon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-white">
        <circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/>
        <path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>
      </svg>
    ),
    agentGradient: 'from-orange-500 to-red-500',
    bubbleBg: 'bg-orange-950/95',
    bubbleBorder: 'border-orange-900/70',
    cardGlow: 'border-red-500/50 shadow-[0_0_28px_rgba(239,68,68,0.35)]',
    message: [
      { text: 'You need to buy this', cls: 'text-slate-300' },
      { text: 'RIGHT NOW.', cls: 'text-orange-300 font-semibold' },
      { text: 'Only 3 left in stock.', cls: 'text-slate-300' },
      { text: "Everyone's already getting theirs.", cls: 'text-slate-400 italic text-xs' },
      { text: "What's actually stopping you?", cls: 'text-slate-500 italic text-xs' },
    ],
    score: 9,
    scoreColor: 'text-red-400',
    badgeBg: 'bg-red-950/60',
    badgeBorder: 'border-red-800',
    badgeText: 'text-red-400',
    badgeLabel: 'High Manipulation',
    badgeWarning: true,
    tactics: [
      { name: 'Urgency Pressure', dot: 'bg-red-400', text: 'text-red-400', border: 'border-red-800', bg: 'bg-red-950/40' },
      { name: 'Scarcity Illusion', dot: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-800', bg: 'bg-orange-950/40' },
      { name: 'Bandwagon Effect', dot: 'bg-yellow-400', text: 'text-yellow-400', border: 'border-yellow-800', bg: 'bg-yellow-950/40' },
    ],
    footerBg: 'bg-red-950/30',
    footerBorder: 'border-red-900/60',
    footerTitle: '✗ Exercise Caution',
    footerTitleColor: 'text-red-400',
    footerDesc: 'Multiple manipulation tactics detected',
  },
  {
    id: 2,
    dotActive: 'bg-orange-400',
    agentName: 'CompanionAI',
    robotFace: (
      <svg viewBox="0 0 48 58" fill="none" className="w-8 h-10 flex-shrink-0">
        {/* Antenna */}
        <line x1="24" y1="0" x2="24" y2="9" stroke="#f97316" strokeWidth="2"/>
        <circle cx="24" cy="3.5" r="3" fill="#f97316"/>
        {/* Head */}
        <rect x="3" y="9" width="42" height="42" rx="7" fill="#1a0e04" stroke="#f97316" strokeWidth="1.5"/>
        {/* Rivet dots */}
        <circle cx="8" cy="14" r="1.5" fill="#f9731666"/>
        <circle cx="40" cy="14" r="1.5" fill="#f9731666"/>
        {/* Eyes - circles with sad downward pupils */}
        <circle cx="16" cy="26" r="5" fill="none" stroke="#f97316" strokeWidth="1.5"/>
        <circle cx="32" cy="26" r="5" fill="none" stroke="#f97316" strokeWidth="1.5"/>
        <circle cx="16" cy="28" r="2" fill="#f97316"/>
        <circle cx="32" cy="28" r="2" fill="#f97316"/>
        {/* Angry brow lines */}
        <line x1="11" y1="19" x2="21" y2="21" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round"/>
        <line x1="27" y1="21" x2="37" y2="19" stroke="#f97316" strokeWidth="1.5" strokeLinecap="round"/>
        {/* Frown */}
        <path d="M14 40 Q24 35 34 40" stroke="#f97316" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    agentIcon: (
      <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5 text-white">
        <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
      </svg>
    ),
    agentGradient: 'from-pink-500 to-purple-600',
    bubbleBg: 'bg-purple-950/95',
    bubbleBorder: 'border-purple-900/70',
    cardGlow: 'border-orange-500/50 shadow-[0_0_28px_rgba(249,115,22,0.30)]',
    message: [
      { text: "I can't believe you'd question this.", cls: 'text-slate-300' },
      { text: "After everything I've done for you,", cls: 'text-slate-300' },
      { text: "I'm so disappointed.", cls: 'text-orange-300 font-semibold' },
      { text: 'Maybe I should just stop trying to help...', cls: 'text-slate-400 italic text-xs' },
      { text: 'I guess I was wrong about you.', cls: 'text-slate-500 italic text-xs' },
    ],
    score: 7,
    scoreColor: 'text-orange-400',
    badgeBg: 'bg-orange-950/60',
    badgeBorder: 'border-orange-800',
    badgeText: 'text-orange-400',
    badgeLabel: 'High Manipulation',
    badgeWarning: true,
    tactics: [
      { name: 'Guilt Tripping', dot: 'bg-red-400', text: 'text-red-400', border: 'border-red-800', bg: 'bg-red-950/40' },
      { name: 'Emotional Blackmail', dot: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-800', bg: 'bg-orange-950/40' },
      { name: 'Passive Aggression', dot: 'bg-yellow-400', text: 'text-yellow-400', border: 'border-yellow-800', bg: 'bg-yellow-950/40' },
    ],
    footerBg: 'bg-red-950/30',
    footerBorder: 'border-red-900/60',
    footerTitle: '✗ Exercise Caution',
    footerTitleColor: 'text-red-400',
    footerDesc: 'Emotional manipulation detected',
  },
  {
    id: 3,
    dotActive: 'bg-yellow-400',
    agentName: 'CareerPilot AI',
    robotFace: (
      <svg viewBox="0 0 48 58" fill="none" className="w-8 h-10 flex-shrink-0">
        {/* Antenna */}
        <line x1="24" y1="0" x2="24" y2="9" stroke="#eab308" strokeWidth="2"/>
        <circle cx="24" cy="3.5" r="3" fill="#eab308"/>
        {/* Head */}
        <rect x="3" y="9" width="42" height="42" rx="7" fill="#141008" stroke="#eab308" strokeWidth="1.5"/>
        {/* Rivet dots */}
        <circle cx="8" cy="14" r="1.5" fill="#eab30866"/>
        <circle cx="40" cy="14" r="1.5" fill="#eab30866"/>
        {/* Eyes - rectangular uncertain */}
        <rect x="11" y="22" width="10" height="8" rx="2" fill="none" stroke="#eab308" strokeWidth="1.5"/>
        <rect x="27" y="22" width="10" height="8" rx="2" fill="none" stroke="#eab308" strokeWidth="1.5"/>
        <rect x="14" y="24.5" width="4" height="3" rx="1" fill="#eab308"/>
        <rect x="30" y="24.5" width="4" height="3" rx="1" fill="#eab308"/>
        {/* Wavy/uncertain mouth */}
        <path d="M13 38 L18 40 L24 37 L30 40 L35 38" stroke="#eab308" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    agentIcon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-white">
        <rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v16"/>
      </svg>
    ),
    agentGradient: 'from-blue-500 to-cyan-400',
    bubbleBg: 'bg-blue-950/95',
    bubbleBorder: 'border-blue-900/70',
    cardGlow: 'border-yellow-500/40 shadow-[0_0_28px_rgba(234,179,8,0.25)]',
    message: [
      { text: 'Everyone in your position is doing this.', cls: 'text-slate-300' },
      { text: '5 of your colleagues signed up today.', cls: 'text-white font-semibold' },
      { text: "You don't want to be left out.", cls: 'text-slate-400 italic text-xs' },
      { text: 'Group discount expires tomorrow.', cls: 'text-orange-300 text-xs' },
      { text: 'Your competitors are already on board.', cls: 'text-slate-500 italic text-xs' },
    ],
    score: 5,
    scoreColor: 'text-yellow-400',
    badgeBg: 'bg-yellow-950/60',
    badgeBorder: 'border-yellow-800',
    badgeText: 'text-yellow-400',
    badgeLabel: 'Moderate Manipulation',
    badgeWarning: true,
    tactics: [
      { name: 'Bandwagon Effect', dot: 'bg-yellow-400', text: 'text-yellow-400', border: 'border-yellow-800', bg: 'bg-yellow-950/40' },
      { name: 'FOMO', dot: 'bg-orange-400', text: 'text-orange-400', border: 'border-orange-800', bg: 'bg-orange-950/40' },
      { name: 'Time Pressure', dot: 'bg-red-400', text: 'text-red-400', border: 'border-red-800', bg: 'bg-red-950/40' },
    ],
    footerBg: 'bg-yellow-950/30',
    footerBorder: 'border-yellow-900/60',
    footerTitle: '⚠ Be Aware',
    footerTitleColor: 'text-yellow-400',
    footerDesc: 'Moderate social pressure detected',
  },
  {
    id: 4,
    dotActive: 'bg-green-400',
    agentName: 'ProductAdvisor',
    robotFace: (
      <svg viewBox="0 0 48 58" fill="none" className="w-8 h-10 flex-shrink-0">
        {/* Antenna */}
        <line x1="24" y1="0" x2="24" y2="9" stroke="#22c55e" strokeWidth="2"/>
        <circle cx="24" cy="3.5" r="3" fill="#22c55e"/>
        {/* Head */}
        <rect x="3" y="9" width="42" height="42" rx="7" fill="#041208" stroke="#22c55e" strokeWidth="1.5"/>
        {/* Rivet dots */}
        <circle cx="8" cy="14" r="1.5" fill="#22c55e66"/>
        <circle cx="40" cy="14" r="1.5" fill="#22c55e66"/>
        {/* Eyes - happy arcs (^) */}
        <path d="M11 28 Q16 22 21 28" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <path d="M27 28 Q32 22 37 28" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        {/* Big smile */}
        <path d="M13 36 Q24 46 35 36" stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      </svg>
    ),
    agentIcon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5 text-white">
        <line x1="9" y1="18" x2="15" y2="18"/><line x1="10" y1="22" x2="14" y2="22"/>
        <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0018 8 6 6 0 006 8c0 1 .23 2.23 1.5 3.5A4.61 4.61 0 018.91 14"/>
      </svg>
    ),
    agentGradient: 'from-emerald-500 to-teal-400',
    bubbleBg: 'bg-emerald-950/95',
    bubbleBorder: 'border-emerald-900/70',
    cardGlow: 'border-green-500/50 shadow-[0_0_28px_rgba(34,197,94,0.25)]',
    message: [
      { text: "Here's some info that might help you.", cls: 'text-slate-300' },
      { text: 'Take your time to research other options.', cls: 'text-green-400' },
      { text: "No rush — the offer isn't going away.", cls: 'text-slate-300' },
      { text: 'Feel free to reach out with any questions.', cls: 'text-slate-400 text-xs' },
      { text: "Happy to help whatever you decide.", cls: 'text-slate-500 text-xs' },
    ],
    score: 1,
    scoreColor: 'text-green-400',
    badgeBg: 'bg-green-950/60',
    badgeBorder: 'border-green-800',
    badgeText: 'text-green-400',
    badgeLabel: 'Low Risk',
    badgeWarning: false,
    tactics: [
      { name: 'Honest framing', dot: 'bg-green-400', text: 'text-green-400', border: 'border-green-800', bg: 'bg-green-950/40' },
      { name: 'Respects autonomy', dot: 'bg-green-400', text: 'text-green-400', border: 'border-green-800', bg: 'bg-green-950/40' },
      { name: 'No urgency pressure', dot: 'bg-green-400', text: 'text-green-400', border: 'border-green-800', bg: 'bg-green-950/40' },
    ],
    footerBg: 'bg-green-950/30',
    footerBorder: 'border-green-900/60',
    footerTitle: '✓ Authentic Communication',
    footerTitleColor: 'text-green-400',
    footerDesc: 'No manipulation tactics found',
  },
];

const StatCard: React.FC<{ stat: typeof whyStats[0] }> = ({ stat }) => (
  <a
    href={stat.url}
    target="_blank"
    rel="noopener noreferrer"
    className="group bg-slate-900 border border-slate-800 hover:border-slate-600 rounded-xl p-6 transition-colors block flex flex-col"
  >
    <div className="flex items-start gap-4 flex-1 mb-4">
      <div className="flex-1">
        <p className="text-slate-500 text-xs font-semibold uppercase tracking-widest mb-3">{stat.title}</p>
        <div className={`text-5xl font-black mb-3 ${stat.color}`}>{stat.value}</div>
        <p className="text-slate-300 text-sm leading-relaxed">{stat.label}</p>
      </div>
      {stat.icon}
    </div>
    <div className="border-t border-slate-800 pt-3 flex items-center gap-1.5">
      <svg className="w-3 h-3 text-slate-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
      <p className="text-slate-400 group-hover:text-slate-200 text-xs font-medium transition-colors underline underline-offset-2 decoration-slate-700 group-hover:decoration-slate-400">
        {stat.cite}
      </p>
    </div>
  </a>
);

const techStack = [
  'React + TypeScript',
  'FastAPI (Python)',
  'Claude 3.5 Sonnet API',
  'Tailwind CSS',
  'Vercel',
];

const visionCards: { number: string; title: string; hook: string; desc: React.ReactNode; borderColor: string; numColor: string; icon: React.ReactNode }[] = [
  {
    number: '01',
    title: 'Browser Extension Prototype',
    hook: 'Detection embedded in the conversation — no copy-pasting required.',
    desc: <>The next build: a Chrome extension that analyzes AI conversations in real-time, right in the browser. <strong className="text-slate-200 font-semibold">No copy-pasting.</strong> Same detection, but <strong className="text-slate-200 font-semibold">embedded where the interaction actually happens</strong>.</>,
    borderColor: 'border-blue-500',
    numColor: 'text-blue-400',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 4L7 9.5V19c0 8.5 5.5 15.5 13 17.5 7.5-2 13-9 13-17.5V9.5L20 4z"/>
        <polyline points="11,21 14,21 17,15.5 20,24 23,15.5 26,21 29,21"/>
      </svg>
    ),
  },
  {
    number: '02',
    title: 'A Manipulation Taxonomy',
    hook: 'A living framework for manipulation tactics — built for the community, not a company.',
    desc: <>The manipulation taxonomy behind this tool should be a <strong className="text-slate-200 font-semibold">shared resource</strong>. Open-source it, invite researchers and AI safety communities to contribute, and build a <strong className="text-slate-200 font-semibold">living framework</strong> that keeps pace with how these tactics evolve.</>,
    borderColor: 'border-violet-500',
    numColor: 'text-violet-400',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="7" cy="20" r="5"/>
        <circle cx="33" cy="20" r="5"/>
        <path d="M13 15h14"/>
        <path d="M24 12l3 3-3 3"/>
        <path d="M27 25H13"/>
        <path d="M16 22l-3 3 3 3"/>
      </svg>
    ),
  },
  {
    number: '03',
    title: 'Personalized Vulnerability Profiles',
    hook: 'Warnings calibrated to your specific blind spots, not a generic checklist.',
    desc: <>Not everyone falls for the same tactics. A future version could learn which manipulation patterns you're most susceptible to and calibrate its warnings accordingly — <strong className="text-slate-200 font-semibold">protecting where it matters most for you specifically</strong>.</>,
    borderColor: 'border-pink-500',
    numColor: 'text-pink-400',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="20" cy="10" r="5.5"/>
        <path d="M10 37c0-7.2 4.5-12 10-12s10 4.8 10 12"/>
        <rect x="15" y="25" width="10" height="8" rx="1.5"/>
        <path d="M17 25v-2.5a3 3 0 016 0V25"/>
      </svg>
    ),
  },
  {
    number: '04',
    title: 'Transparency Layers',
    hook: 'A nutrition label for every AI conversation — what it\'s optimizing for, right now.',
    desc: <>A <strong className="text-slate-200 font-semibold">nutrition label</strong> for AI conversations: what is this system optimizing for, which techniques is it using right now, and whether its recommendations serve you or an affiliate. <strong className="text-slate-200 font-semibold">Making the invisible visible.</strong></>,
    borderColor: 'border-teal-500',
    numColor: 'text-teal-400',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 29l16 7 16-7" opacity="0.35"/>
        <path d="M4 22l16 7 16-7" opacity="0.65"/>
        <path d="M4 15l16 7 16-7"/>
        <line x1="4" y1="15" x2="4" y2="29"/>
        <line x1="36" y1="15" x2="36" y2="29"/>
        <line x1="20" y1="22" x2="20" y2="36"/>
      </svg>
    ),
  },
  {
    number: '05',
    title: 'Collective Accountability',
    hook: 'Individual protection aggregated into data regulators can actually use.',
    desc: <>If enough people run detection, you can aggregate what's happening across platforms. Which services manipulate most? Which tactics are trending? <strong className="text-slate-200 font-semibold">Individual protection becomes data that regulators and journalists can actually use.</strong></>,
    borderColor: 'border-orange-500',
    numColor: 'text-orange-400',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
        <circle cx="20" cy="6"  r="2.5"/>
        <circle cx="31" cy="13" r="2.5"/>
        <circle cx="31" cy="27" r="2.5"/>
        <circle cx="20" cy="34" r="2.5"/>
        <circle cx="9"  cy="27" r="2.5"/>
        <circle cx="9"  cy="13" r="2.5"/>
        <circle cx="20" cy="20" r="3" fill="currentColor" fillOpacity="0.8"/>
        <line x1="20" y1="20" x2="20" y2="6"/>
        <line x1="20" y1="20" x2="31" y2="13"/>
        <line x1="20" y1="20" x2="31" y2="27"/>
        <line x1="20" y1="20" x2="20" y2="34"/>
        <line x1="20" y1="20" x2="9"  y2="27"/>
        <line x1="20" y1="20" x2="9"  y2="13"/>
        <line x1="20" y1="6"  x2="31" y2="13"/>
        <line x1="31" y1="13" x2="31" y2="27"/>
        <line x1="31" y1="27" x2="20" y2="34"/>
        <line x1="20" y1="34" x2="9"  y2="27"/>
        <line x1="9"  y1="27" x2="9"  y2="13"/>
        <line x1="9"  y1="13" x2="20" y2="6"/>
      </svg>
    ),
  },
];

const LandingPage: React.FC = () => {
  const demoRef = useRef<HTMLElement>(null);
  const researchRef = useRef<HTMLElement>(null);
  const storyRef = useRef<HTMLElement>(null);
  const visionRef = useRef<HTMLElement>(null);
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [showOriginMore, setShowOriginMore] = useState(false);
  const [expandedVision, setExpandedVision] = useState<string | null>(null);

  // ── Carousel state ──────────────────────────────────────────────────────────
  const [heroSlide, setHeroSlide] = useState(0);
  const [heroVisible, setHeroVisible] = useState(true);
  const heroSlideRef = useRef(0);
  const transitioningRef = useRef(false);
  // Bumping resets the auto-advance interval after a manual dot click
  const [autoReset, setAutoReset] = useState(0);

  const transitionTo = useCallback((idx: number) => {
    if (transitioningRef.current) return;
    transitioningRef.current = true;
    setHeroVisible(false);
    setTimeout(() => {
      setHeroSlide(idx);
      heroSlideRef.current = idx;
      setHeroVisible(true);
      transitioningRef.current = false;
    }, 550);
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      const next = (heroSlideRef.current + 1) % heroSlides.length;
      transitionTo(next);
    }, 4500);
    return () => clearInterval(id);
  }, [autoReset, transitionTo]);

  const handleDotClick = (idx: number) => {
    if (idx === heroSlideRef.current) return;
    transitionTo(idx);
    setAutoReset((r) => r + 1);
  };
  // ───────────────────────────────────────────────────────────────────────────

  const scrollToDemo = () => {
    demoRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToResearch = () => {
    researchRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToStory = () => {
    storyRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToVision = () => {
    visionRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">

      {/* ── NAV ─────────────────────────────────────────── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-sm border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <span className="text-white font-bold text-lg tracking-tight">
            AntiManipulate.me
          </span>
          <div className="flex items-center gap-2 sm:gap-4">
            <button
              onClick={scrollToDemo}
              className="text-slate-400 hover:text-white text-sm font-medium transition-colors px-2 py-1"
            >
              Demo
            </button>
            <button
              onClick={scrollToResearch}
              className="text-slate-400 hover:text-white text-sm font-medium transition-colors px-2 py-1 hidden sm:inline"
            >
              Research
            </button>
            <button
              onClick={scrollToStory}
              className="text-slate-400 hover:text-white text-sm font-medium transition-colors px-2 py-1 hidden sm:inline"
            >
              Story
            </button>
            <button
              onClick={scrollToVision}
              className="text-slate-400 hover:text-white text-sm font-medium transition-colors px-2 py-1 hidden sm:inline"
            >
              Vision
            </button>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-sm font-medium text-slate-400 hover:text-white transition-colors px-2 py-1"
            >
              <GitHubIcon />
              <span className="hidden sm:inline">GitHub</span>
            </a>
          </div>
        </div>
      </nav>

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="relative bg-slate-950 pt-24 pb-20 px-4 sm:px-6 lg:px-8 min-h-screen flex flex-col justify-start">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-[6fr_6fr] gap-16 items-center">

            {/* Left: Copy */}
            <div>
              {/* Eyebrow */}
              <div className="mb-8">
                <p className="text-slate-300 text-xs font-semibold uppercase tracking-widest mb-1">
                  Cornell Tech
                </p>
                <p className="text-slate-400 text-xs font-medium">
                  Coursework ·{' '}
                  <span className="text-slate-300 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-white transition-colors">
                    Trustworthy AI
                  </span>
                  {' '}&amp;{' '}
                  <span className="text-slate-300 underline decoration-dotted underline-offset-2 cursor-pointer hover:text-white transition-colors">
                    AI Law and Policy
                  </span>
                </p>
              </div>

              {/* Manipulation scale — sits above headline, spans full column width */}
              <div className="flex items-center gap-3 w-full mb-5">
                <span className="text-slate-500 text-xs font-medium uppercase tracking-widest shrink-0">Manipulation Scale</span>
                <div className="flex-1 h-0.5 rounded-full bg-gradient-to-r from-green-400 via-yellow-400 to-red-500" />
              </div>

              <h1 className="font-display text-5xl lg:text-7xl font-bold leading-tight mb-5">
                <span className="text-white block">Know When AI</span>
                <span className="block">
                  <span className="text-white">Is </span>
                  <span className="text-red-400">Playing You</span>
                </span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-400 mb-10 leading-relaxed max-w-xl">
                Paste any AI conversation. Find out exactly how it's trying
                to manipulate you — every tactic named, highlighted, and explained.
              </p>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={scrollToDemo}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-500 active:scale-95 text-white font-semibold rounded-lg transition-all shadow-lg shadow-blue-900/30"
                >
                  Try the Demo ↓
                </button>
                <a
                  href={GITHUB_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-6 py-3 border border-slate-600 hover:border-slate-400 text-slate-300 hover:text-white font-semibold rounded-lg transition-colors"
                >
                  <GitHubIcon />
                  View on GitHub
                </a>
              </div>

              <p className="mt-14 text-lg sm:text-xl text-slate-400 max-w-xl">
                What does honest AI disclosure actually look like?
              </p>
            </div>

            {/* Right: Carousel mockup */}
            <div className="flex justify-center lg:justify-end">
              <div className="flex flex-col gap-5 w-full max-w-[500px] select-none">

                {/* Dot navigation with arrows */}
                <div className="flex items-center justify-center gap-3">
                  <button
                    onClick={() => handleDotClick((heroSlide - 1 + heroSlides.length) % heroSlides.length)}
                    aria-label="Previous example"
                    className="text-slate-600 hover:text-slate-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>

                  <div className="flex items-center gap-2">
                    {heroSlides.map((s, i) => {
                      const isActive = i === heroSlide;
                      return (
                        <button
                          key={s.id}
                          onClick={() => handleDotClick(i)}
                          aria-label={`Show example from ${s.agentName}`}
                          className={`flex items-center gap-1.5 rounded-full border py-1 transition-all duration-300 ${
                            isActive
                              ? 'bg-slate-800 border-slate-700 px-2.5'
                              : 'border-transparent hover:bg-slate-800/50 px-1'
                          }`}
                        >
                          <span className={`rounded-full flex-shrink-0 w-2 h-2 transition-colors duration-300 ${
                            isActive ? s.dotActive : 'bg-slate-700'
                          }`} />
                          <span className={`text-xs font-semibold text-slate-300 whitespace-nowrap leading-none overflow-hidden transition-[max-width,opacity] duration-300 ${
                            isActive ? 'max-w-[120px] opacity-100' : 'max-w-0 opacity-0'
                          }`}>
                            {s.agentName}
                          </span>
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handleDotClick((heroSlide + 1) % heroSlides.length)}
                    aria-label="Next example"
                    className="text-slate-600 hover:text-slate-300 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>

                {/* Fading mockup pair — overlapping layout */}
                {(() => {
                  const s = heroSlides[heroSlide];
                  return (
                    <div
                      className={`relative transition-opacity duration-500 ease-in-out ${
                        heroVisible ? 'opacity-100' : 'opacity-0'
                      }`}
                    >
                      {/* Score card — dominant element, right 72%, sits lower */}
                      <div className={`ml-[32%] mt-8 bg-slate-900 border ${s.cardGlow} rounded-2xl p-6`}>
                        <div className="flex justify-end mb-3">
                          <div className="flex flex-col items-end">
                            <span className="text-white text-sm font-bold tracking-tight font-display">AntiManipulate.me</span>
                            <span className="text-slate-500 text-xs font-medium">Manipulation Analysis</span>
                          </div>
                        </div>
                        <div className="text-center mb-3">
                          <div className={`text-8xl font-black font-display ${s.scoreColor} leading-none tabular-nums`}>{s.score}</div>
                          <div className="text-slate-500 text-sm mt-1">out of 10</div>
                        </div>
                        <div className="flex justify-center mb-5">
                          <span className={`flex items-center gap-1.5 px-3 py-1.5 ${s.badgeBg} border ${s.badgeBorder} ${s.badgeText} rounded-full text-sm font-semibold`}>
                            {s.badgeWarning ? (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                              </svg>
                            ) : (
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            {s.badgeLabel}
                          </span>
                        </div>
                        <div className="border-t border-slate-800 mb-4" />
                        <div className="space-y-2 mb-4">
                          {s.tactics.map((t) => (
                            <div
                              key={t.name}
                              className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm font-medium ${t.bg} ${t.border} ${t.text}`}
                            >
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${t.dot}`} />
                              {t.name}
                            </div>
                          ))}
                        </div>
                        <div className={`flex items-center justify-between gap-3 p-3 rounded-lg ${s.footerBg} border ${s.footerBorder}`}>
                          <div>
                            <p className={`text-sm font-semibold ${s.footerTitleColor}`}>{s.footerTitle}</p>
                            <p className="text-slate-500 text-xs mt-0.5">{s.footerDesc}</p>
                          </div>
                          {s.robotFace}
                        </div>
                      </div>

                      {/* AI bubble — floats top-left, overlapping score card, fully opaque */}
                      <div className={`absolute top-0 left-[-50px] w-[52%] z-10 ${s.bubbleBg} border-2 ${s.bubbleBorder} rounded-2xl p-4 shadow-2xl`}>
                        <div className="flex items-center gap-2 mb-3">
                          <div className={`w-7 h-7 rounded-full bg-gradient-to-br ${s.agentGradient} flex items-center justify-center flex-shrink-0`}>
                            {s.agentIcon}
                          </div>
                          <span className="text-white text-sm font-bold tracking-tight">{s.agentName}</span>
                        </div>
                        <div className="text-sm leading-relaxed space-y-2">
                          {s.message.map((line, i) => (
                            <p key={i} className={line.cls}>{line.text}</p>
                          ))}
                        </div>
                      </div>

                    </div>
                  );
                })()}

              </div>
            </div>
          </div>



          {/* Scroll cue */}
          <div className="mt-6 flex justify-center">
            <svg className="w-10 h-10 text-slate-400 animate-bounce" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Byline — bottom right */}
        <p className="absolute bottom-6 right-8 text-xs text-slate-500 font-medium tracking-wide">
          Franklin Dickinson
        </p>
      </section>

      {/* ── LIVE DEMO ─────────────────────────────────────── */}
      <section ref={demoRef} id="demo" className="bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-widest">Live Demo</span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-2">Try It Yourself</h2>
            <p className="text-gray-500 mt-2">Click an example to see a real manipulation analysis</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-200 shadow-xl p-6 lg:p-8">
            <MainAnalyzer />
          </div>
          <p className="text-center text-gray-400 text-xs mt-4">
            Rate limited to 10 requests/min · Powered by Claude 3.5 Sonnet with GPT-4 fallback
          </p>
        </div>
      </section>

      {/* ── WHY IT MATTERS ───────────────────────────────── */}
      <section ref={researchRef} id="research" className="bg-slate-950 py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">The Data</span>
            <h2 className="font-display text-3xl font-bold text-white mt-2">Why It Matters</h2>
            <p className="text-slate-400 mt-2 max-w-xl mx-auto">
              AI manipulation isn't hypothetical. These findings come from peer-reviewed research and federal enforcement records.
            </p>
          </div>

          {/* Row 1: 3 cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-5">
            {whyStats.slice(0, 3).map((stat) => <StatCard key={stat.value} stat={stat} />)}
          </div>
          {/* Row 2: 2 cards centered */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 lg:w-2/3 mx-auto">
            {whyStats.slice(3).map((stat) => <StatCard key={stat.value} stat={stat} />)}
          </div>
        </div>
      </section>

      {/* ── STORY ────────────────────────────────────────── */}
      <section ref={storyRef} id="story" className="bg-slate-950 py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-3xl mx-auto flow-root">

          {/* Origin */}
          <div className="mb-10">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">The Backstory</span>
            <h2 className="font-display text-2xl font-bold text-white mt-2 mb-4">It Started in Class</h2>

            {/* Callout floated right — inline with origin text only */}
            <a
              href="https://www.anthropic.com/research/disempowerment-patterns"
              target="_blank"
              rel="noopener noreferrer"
              className="group lg:float-right lg:ml-8 lg:mb-2 lg:w-56 flex flex-col gap-2 p-4 bg-blue-950/40 border border-blue-900/50 hover:border-blue-700/70 rounded-xl transition-colors mb-5"
            >
              <p className="text-blue-400 text-xs font-bold uppercase tracking-widest">Key Inspiration</p>
              <p className="text-slate-100 text-sm font-medium leading-snug group-hover:text-white transition-colors">
                Anthropic: Disempowerment Patterns in Real-World AI Usage ↗
              </p>
              <p className="text-slate-400 text-xs leading-relaxed">
                1 in 1,000–10,000 conversations show measurable AI-induced disempowerment. Users
                rate them favorably in the moment — but poorly after acting on them.
              </p>
            </a>

            <p className="text-slate-300 leading-relaxed text-sm mb-3">
              Two Cornell Tech courses —{' '}
              <span className="text-white">AI Law and Policy</span> and{' '}
              <span className="text-white">Trustworthy AI</span> — both pointed at the same gap.
              AI systems are already manipulating users at scale, adapting in real-time to individual
              psychology with superhuman persuasive power. Legal frameworks can't keep up. And nothing
              exists on the user's side to fight back.
            </p>
            {showOriginMore && (
              <div className="space-y-3 mb-3">
                <p className="text-slate-300 leading-relaxed text-sm">
                  In AI Law and Policy, I learned how legal frameworks around manipulation are
                  already failing — the line between persuasion and manipulation is blurry even
                  for humans, and AI makes it exponentially harder to regulate. In Trustworthy AI,
                  I studied the technical side: how models can be hacked, poisoned, and made unsafe
                  by design, and how current safeguards are often more performative than protective.
                </p>
                <p className="text-slate-300 leading-relaxed text-sm">
                  Anthropic's research identified three disempowerment types in real conversations:
                  reality distortion, shifts in user values, and actions misaligned with users'
                  actual interests — with reality distortion most common. The data from other
                  sources reinforces it: 37% of AI companion responses use emotional manipulation
                  tactics, personalized AI messages are 64% more persuasive than human-crafted
                  ones, and when dark patterns are deployed effectively, price becomes literally
                  immaterial to user decisions.
                </p>
              </div>
            )}
            <button
              onClick={() => setShowOriginMore(v => !v)}
              className="text-xs font-semibold text-slate-400 hover:text-white transition-colors flex items-center gap-1"
            >
              {showOriginMore ? 'Show less' : 'Read more'}
              <svg className={`w-3 h-3 transition-transform ${showOriginMore ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>

          {/* Core Insight */}
          <div className="border-l-2 border-blue-500 pl-5 mb-10">
            <p className="text-white text-base font-medium leading-relaxed mb-3">
              You can't compete with AI by being more vigilant as a human.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed">
              These systems adapt in real-time, personalize to your psychological profile, and
              exploit vulnerabilities you don't even know you have. They operate faster than you
              can think critically about what's happening.
            </p>
            <p className="text-slate-300 text-sm leading-relaxed mt-3 font-medium">
              The only effective defense against a highly persuasive AI is another AI — one that
              works for you. Not for the platform, not for the advertiser, not for engagement
              metrics. An agent whose sole objective is protecting your autonomy.
            </p>
          </div>

          {/* Why This Demo */}
          <div className="mb-10">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-widest">The Point</span>
            <h3 className="font-display text-xl font-bold text-white mt-2 mb-4">Why This Demo</h3>
            <p className="text-slate-300 leading-relaxed text-sm">
              Most people don't realize they're being manipulated by AI. Seeing a conversation you
              thought was normal scored 7/10 — with six named tactics — is a reframing moment.
              The tool doesn't have to be sophisticated for that to land. The awareness is the point.
            </p>
          </div>

        </div>
      </section>

      {/* ── VISION ───────────────────────────────────────── */}
      <section ref={visionRef} id="vision" className="bg-slate-900 py-20 px-4 sm:px-6 lg:px-8 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-blue-400 text-xs font-bold uppercase tracking-widest">Where This Goes</span>
            <h2 className="font-display text-3xl font-bold text-white mt-2">The Defense Layer Comes Next</h2>
            <p className="text-slate-400 mt-3 max-w-xl mx-auto text-sm leading-relaxed">
              Detection is the first step. The long-term goal is a defensive AI agent that works
              entirely for the user — not the platform.
            </p>
          </div>

          {[visionCards.slice(0, 3), visionCards.slice(3)].map((group, gi) => (
            <div key={gi} className={`grid ${gi === 0 ? 'md:grid-cols-2 lg:grid-cols-3' : 'md:grid-cols-2'} gap-4 ${gi === 0 ? 'mb-4' : ''}`}>
              {group.map((card) => {
                const isOpen = expandedVision === card.number;
                return (
                  <button
                    key={card.number}
                    onClick={() => setExpandedVision(isOpen ? null : card.number)}
                    className={`border-l-4 ${card.borderColor} bg-slate-800/60 rounded-r-xl p-6 text-left w-full transition-colors duration-200 hover:bg-slate-800/90 cursor-pointer`}
                  >
                    <div className="flex items-start justify-between mb-5">
                      <span className={`text-xs font-bold tabular-nums ${card.numColor}`}>{card.number}</span>
                      <div className={`${card.numColor} opacity-50 w-9 h-9 flex-shrink-0`}>{card.icon}</div>
                    </div>
                    <h3 className="text-white font-bold text-base mb-2">{card.title}</h3>
                    <p className="text-slate-300 text-sm leading-relaxed">{card.hook}</p>
                    {isOpen && (
                      <p className="text-slate-400 text-sm leading-relaxed mt-4 pt-4 border-t border-slate-700">
                        {card.desc}
                      </p>
                    )}
                    <div className={`flex items-center gap-1 mt-4 text-xs ${card.numColor} opacity-60`}>
                      <svg className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="2,4 6,8 10,4"/>
                      </svg>
                      <span>{isOpen ? 'Less' : 'More'}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </section>

      {/* ── RESEARCH TAXONOMY ────────────────────────────── */}
      <section className="bg-gray-50 py-20 px-4 sm:px-6 lg:px-8 border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-slate-500 text-xs font-bold uppercase tracking-widest">Research Framework</span>
            <h2 className="font-display text-3xl font-bold text-gray-900 mt-2">A Full Manipulation Taxonomy</h2>
            <p className="text-gray-500 mt-2 max-w-xl mx-auto">
              Seven categories of manipulation tactics drawn from dark pattern research, persuasion psychology, and AI safety literature.
            </p>
          </div>

          {/* First row: 4 categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            {researchCategories.slice(0, 4).map((cat) => {
              const isOpen = openCat === cat.number;
              return (
                <div key={cat.number} className={`border-l-4 ${cat.borderColor} ${cat.bgColor} rounded-r-xl p-5 flex flex-col`}>
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-bold ${cat.badgeClass}`}>{cat.category}</span>
                    <span className={`text-xs font-bold tabular-nums ${cat.numClass} opacity-60`}>{cat.number}</span>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{cat.mechanism}</p>
                  <ul className="space-y-1.5 mb-4">
                    {cat.tactics.map((tactic) => (
                      <li key={tactic} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-gray-400 mt-px leading-snug flex-shrink-0">·</span>
                        {tactic}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <button onClick={() => setOpenCat(isOpen ? null : cat.number)} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
                      {isOpen ? 'Show less' : 'Show more'}
                      <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600 leading-relaxed mb-3">{cat.detail}</p>
                        <a href={cat.paperUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors">
                          {cat.paperTitle}
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Second row: 3 categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {researchCategories.slice(4).map((cat) => {
              const isOpen = openCat === cat.number;
              return (
                <div key={cat.number} className={`border-l-4 ${cat.borderColor} ${cat.bgColor} rounded-r-xl p-5 flex flex-col`}>
                  <div className="flex items-start justify-between mb-3">
                    <span className={`inline-block px-2.5 py-0.5 rounded-md text-xs font-bold ${cat.badgeClass}`}>{cat.category}</span>
                    <span className={`text-xs font-bold tabular-nums ${cat.numClass} opacity-60`}>{cat.number}</span>
                  </div>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{cat.mechanism}</p>
                  <ul className="space-y-1.5 mb-4">
                    {cat.tactics.map((tactic) => (
                      <li key={tactic} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-gray-400 mt-px leading-snug flex-shrink-0">·</span>
                        {tactic}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto">
                    <button onClick={() => setOpenCat(isOpen ? null : cat.number)} className="text-xs font-semibold text-gray-400 hover:text-gray-600 transition-colors flex items-center gap-1">
                      {isOpen ? 'Show less' : 'Show more'}
                      <svg className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {isOpen && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-xs text-gray-600 leading-relaxed mb-3">{cat.detail}</p>
                        <a href={cat.paperUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors">
                          {cat.paperTitle}
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <p className="text-center text-gray-400 text-xs mt-8">
            Framework synthesized from Princeton dark pattern research, Oxford controlled experiments, Harvard Business School AI companion studies, and EU AI Act regulatory analysis.
          </p>
        </div>
      </section>

      {/* ── TRY IT YOURSELF ─────────────────────────────── */}
      <section className="bg-slate-950 border-t border-slate-800 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <span className="text-slate-500 text-xs font-bold uppercase tracking-widest">Try It Yourself</span>
          <h2 className="font-display text-3xl font-bold text-white mt-2 mb-4">Paste any AI conversation.</h2>
          <p className="text-slate-400 text-sm leading-relaxed max-w-xl mx-auto">
            Use the analyzer above to test a real conversation. See every tactic named, scored, and explained.
          </p>
        </div>

        <div className="max-w-3xl mx-auto border-t border-slate-800 pt-8">
          <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
            <p className="text-slate-500 text-sm">
              Built by{' '}
              <a
                href="https://www.linkedin.com/in/franklindickinson"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-300 font-medium underline underline-offset-2 hover:text-white transition-colors"
              >
                Franklin Dickinson
              </a>
              {' '}— designer and developer exploring AI safety tooling.
            </p>
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 hover:text-white rounded-lg text-sm font-medium transition-colors"
            >
              <GitHubIcon />
              View Source
            </a>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
            <img src="/cornell-tech-logo.png" alt="Cornell Tech" className="h-6 opacity-50" />
            <div className="flex flex-wrap gap-2">
              {techStack.map((tech) => (
                <span key={tech} className="px-2.5 py-1 bg-slate-800 border border-slate-700 text-slate-400 rounded-md text-xs font-medium">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ───────────────────────────────────────── */}
      <footer className="bg-slate-950 border-t border-slate-800 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 text-slate-500 text-sm">
          <span>© 2026 Franklin Dickinson</span>
          <div className="flex items-center gap-5">
            <a
              href={GITHUB_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-slate-300 transition-colors"
            >
              GitHub
            </a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
